/**
 * Date helpers for the roster grid.
 *
 * Everything here works on plain `YYYY-MM-DD` strings, never Date objects in
 * local time. A roster day is a calendar date, not an instant: "Tuesday the
 * 15th" is the same shift whether the browser is in Melbourne or on a phone
 * that has wandered into another timezone. Using `new Date('2026-09-15')` and
 * formatting it locally is how scheduling apps end up showing Monday to someone
 * in Perth.
 *
 * The legacy calendar had the opposite problem from the other direction: three
 * different date columns per row, two of which disagreed with each other on 57%
 * of rows.
 */

/** A plain calendar date, `YYYY-MM-DD`. */
export type IsoDate = string;

const DAY_MS = 86_400_000;

/** Today in the business timezone, as an ISO date. */
export function today(timeZone = 'Australia/Melbourne'): IsoDate {
    // en-CA formats as YYYY-MM-DD, which is the shortest correct way to get a
    // timezone-aware calendar date out of Intl.
    return new Intl.DateTimeFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone,
    }).format(new Date());
}

/** Parse to a UTC-midnight Date, so arithmetic never crosses a DST boundary. */
function toUtc(date: IsoDate): Date {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1));
}

function fromUtc(d: Date): IsoDate {
    return d.toISOString().slice(0, 10);
}

export function addDays(date: IsoDate, days: number): IsoDate {
    return fromUtc(new Date(toUtc(date).getTime() + days * DAY_MS));
}

export function diffDays(from: IsoDate, to: IsoDate): number {
    return Math.round((toUtc(to).getTime() - toUtc(from).getTime()) / DAY_MS);
}

/** 0 = Sunday ... 6 = Saturday. */
export function dayOfWeek(date: IsoDate): number {
    return toUtc(date).getUTCDay();
}

export function isWeekend(date: IsoDate): boolean {
    const d = dayOfWeek(date);
    return d === 0 || d === 6;
}

/** The Monday on or before `date`. Australian rosters run Monday to Sunday. */
export function startOfWeek(date: IsoDate): IsoDate {
    const dow = dayOfWeek(date);
    return addDays(date, dow === 0 ? -6 : 1 - dow);
}

export function startOfMonth(date: IsoDate): IsoDate {
    return `${date.slice(0, 7)}-01`;
}

export function endOfMonth(date: IsoDate): IsoDate {
    const d = toUtc(date);
    return fromUtc(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)));
}

/** Inclusive range, as a list of dates. */
export function rangeOf(from: IsoDate, to: IsoDate): IsoDate[] {
    const out: IsoDate[] = [];
    for (let d = from; d <= to; d = addDays(d, 1)) {
        out.push(d);
    }
    return out;
}

const DOW_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** "Tue" */
export function formatDow(date: IsoDate): string {
    return DOW_SHORT[dayOfWeek(date)] ?? '';
}

/** "15" */
export function formatDayNum(date: IsoDate): string {
    return String(Number(date.slice(8, 10)));
}

/** "Sep 2026" */
export function formatMonthYear(date: IsoDate): string {
    const m = Number(date.slice(5, 7)) - 1;
    return `${MONTH_SHORT[m] ?? ''} ${date.slice(0, 4)}`;
}

/**
 * "24 Sep 2026" — Australian order, day before month, with the year.
 *
 * For dates the user reads rather than dates the app compares. The month is
 * written rather than numeric on purpose: `09/10/2026` is the 9th of October
 * here and the 10th of September to anyone reading it as American, and leave
 * dates are exactly the kind of thing that gets forwarded in an email.
 *
 * Built from the string, never `new Date(iso)`, for the reason at the top of
 * this file — that parses as UTC midnight and formats in the browser's zone,
 * which shows the previous day to anyone west of Melbourne.
 */
export function formatAu(date: IsoDate): string {
    const m = Number(date.slice(5, 7)) - 1;
    return `${formatDayNum(date)} ${MONTH_SHORT[m] ?? ''} ${date.slice(0, 4)}`;
}

/**
 * "24 Sep 2026" for one day, "24 Sep – 28 Sep 2026" for a span.
 *
 * The year appears once, at the end, unless the range crosses into another one.
 */
export function formatAuRange(from: IsoDate, to: IsoDate): string {
    if (from === to) {
        return formatAu(from);
    }
    if (from.slice(0, 4) === to.slice(0, 4)) {
        const m = Number(from.slice(5, 7)) - 1;
        return `${formatDayNum(from)} ${MONTH_SHORT[m] ?? ''} – ${formatAu(to)}`;
    }
    return `${formatAu(from)} – ${formatAu(to)}`;
}

/** "Tue 15 Sep" */
export function formatShort(date: IsoDate): string {
    const m = Number(date.slice(5, 7)) - 1;
    return `${formatDow(date)} ${formatDayNum(date)} ${MONTH_SHORT[m] ?? ''}`;
}

/** The Sunday ending the week that contains `date`. */
export function endOfWeek(date: IsoDate): IsoDate {
    return addDays(startOfWeek(date), 6);
}

/**
 * The quick ranges from the legacy toolbar, which had eight buttons for them.
 *
 * Kept because they match how the office talks about the board ("pull up last
 * week"), and computed rather than hard-coded so they stay correct across month
 * and year boundaries — the legacy versions were built with string maths on
 * d/m/Y and broke either side of January.
 */
export interface QuickRange {
    readonly id: string;
    readonly label: string;
    readonly of: (today: IsoDate) => { from: IsoDate; to: IsoDate };
}

export const QUICK_RANGES: readonly QuickRange[] = [
    { id: 'today', label: 'Today', of: (t) => ({ from: t, to: t }) },
    { id: 'yesterday', label: 'Yesterday', of: (t) => ({ from: addDays(t, -1), to: addDays(t, -1) }) },
    { id: 'this-week', label: 'This week', of: (t) => ({ from: startOfWeek(t), to: endOfWeek(t) }) },
    {
        id: 'last-week',
        label: 'Last week',
        of: (t) => ({ from: addDays(startOfWeek(t), -7), to: addDays(startOfWeek(t), -1) }),
    },
    { id: 'this-month', label: 'This month', of: (t) => ({ from: startOfMonth(t), to: endOfMonth(t) }) },
    {
        id: 'last-month',
        label: 'Last month',
        of: (t) => {
            const prev = addDays(startOfMonth(t), -1);
            return { from: startOfMonth(prev), to: endOfMonth(prev) };
        },
    },
    {
        id: 'three-months',
        label: '3 months',
        of: (t) => ({ from: startOfMonth(t), to: endOfMonth(addDays(endOfMonth(addDays(endOfMonth(t), 1)), 1)) }),
    },
];

// ─── Pay periods ────────────────────────────────────────────────────────────

/**
 * The pay period containing `date`: a Monday-to-Sunday fortnight.
 *
 * Periods run in `days` steps from the MONDAY of the week holding `anchor`
 * (company_details.payroll_first_pay_date, 2018-08-16 in production), so the
 * cycle cannot drift.
 *
 * The anchor itself is a Thursday — the first pay DATE, not the first period
 * start. An earlier version started periods on it, Thursday to Wednesday, and
 * every figure was out of step with payroll: all 1,297 fortnights the legacy
 * app sent to Xero (xero_upload, 2023–2026) run Monday to Sunday, starting 11
 * days after the anchor — which is the Monday of its week, 2018-08-13, plus
 * whole fortnights. The legacy Timesheet date picker allowed only Mondays for
 * the same reason.
 */
export function payPeriodFor(
    date: IsoDate,
    anchor: IsoDate,
    days: number,
): { from: IsoDate; to: IsoDate } {
    const length = days > 0 ? days : 14;
    const start = startOfWeek(anchor);
    const elapsed = diffDays(start, date);
    // Floor, so dates before the anchor land in earlier periods rather than
    // rounding toward it — a timesheet can legitimately predate the anchor.
    const index = Math.floor(elapsed / length);
    const from = addDays(start, index * length);
    return { from, to: addDays(from, length - 1) };
}

/** Step whole pay periods. */
export function shiftPayPeriod(
    period: { from: IsoDate; to: IsoDate },
    direction: -1 | 1,
    days: number,
): { from: IsoDate; to: IsoDate } {
    const length = days > 0 ? days : 14;
    const from = addDays(period.from, direction * length);
    return { from, to: addDays(from, length - 1) };
}
